import TableHeader from "./feature/rosterTable/TableHeader";
import TableBody_wanted from "./feature/rosterTable/TableBody_wanted";
import "@css/modal_submittedWantedList.css";
import type * as type from "@vTypes";
import { X } from "lucide-react";
import { useRosterSubmissionQuery } from "@hooks/roster-craete/useRosterSubmissionQuery";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useGroupStore } from "@store/groupStore.ts";
import { useQuery } from "@tanstack/react-query";
import { weeklyOffNurses } from "@api/nurses";
import type { NurseResponse } from "@/types/nurse-type";
import CustomModal from "@/components/modal/CustomModal";
import { useWantedShiftRequestsQuery } from "@/hooks/roster-wanted/useWantedShiftRequestsQuery";
import { useEffect, useMemo, useState } from "react";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { mapShiftRequestsToPreferenceSchedule } from "@/utils/mapShiftRequestsToPreferenceSchedule";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";

function ModalSubmittedWantedlist({
  defaultShiftCode,
  currentScheduleInfo,
  holidayDates,
  fullWanted,
  preferenceSchedule,
  nurses,
  shiftInfo,
  onClose,
  isViewOnlyOff,
  showWeeklyOff,
}: {
  defaultShiftCode: type.Type_availableShifts[];
  currentScheduleInfo: { year: number; month: number; version: number };
  holidayDates: string[] | null;
  fullWanted: type.WantedAll[];
  preferenceSchedule: type.PreferenceSchedule[];
  nurses: NurseResponse[] | null;
  shiftInfo: type.Type_availableShifts[];
  onClose: () => void;
  isViewOnlyOff: boolean;
  showWeeklyOff?: boolean;
}) {
  const isAdminHook = useIsAdmin();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();

  const { data: shiftRequestsData, isLoading: isShiftRequestsLoading } =
    useWantedShiftRequestsQuery({
      year: currentScheduleInfo.year,
      month: currentScheduleInfo.month,
      groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
      enabled: !!(
        nurses?.length &&
        currentScheduleInfo.year &&
        currentScheduleInfo.month
      ),
    });

  const wantedAssignmentsByNurse = useMemo(() => {
    const map = new Map<string, InboundItem[]>();
    const results = shiftRequestsData?.results ?? [];
    for (const r of results) {
      const rowNurseId = String(r.nurse_id ?? "").trim();
      const rowAssignments = r.assignments ?? [];
      for (const a of rowAssignments) {
        const nurseId = String(a.nurse_id ?? rowNurseId).trim();
        if (nurseId === "") continue;
        const startDate = a.start_date ?? a.startDate ?? "";
        if (startDate === "") continue;
        const prev = map.get(nurseId) ?? [];
        prev.push({
          startDate,
          endDate: a.end_date ?? a.endDate ?? null,
          reason: a.reason,
          target_group_id: a.target_group_id,
          target_group_name: a.target_group_name,
        });
        map.set(nurseId, prev);
      }
    }
    return map;
  }, [shiftRequestsData?.results]);

  const { data: shiftsData } = useShiftsQuery(
    isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined
  );
  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    defaultShiftCode as unknown as type.availableShifts[],
    shiftsData ?? null
  );

  const preferenceScheduleForTable = useMemo(() => {
    const base = preferenceSchedule ?? [];
    const results = shiftRequestsData?.results;
    if (!nurses?.length || !results?.length) {
      return base;
    }
    return mapShiftRequestsToPreferenceSchedule(
      nurses,
      base,
      results,
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      shiftLookupMaps
    );
  }, [
    nurses,
    preferenceSchedule,
    shiftRequestsData,
    currentScheduleInfo.year,
    currentScheduleInfo.month,
    shiftLookupMaps,
  ]);

  const { data: submissionData, isLoading: isSubmissionLoading } =
    useRosterSubmissionQuery({
      year: currentScheduleInfo.year,
      month: currentScheduleInfo.month,
      groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
    });
  const currentSubmittedList = submissionData?.submitted_nurses ?? [];

  const [orderedNurses, setOrderedNurses] = useState<NurseResponse[]>([]);
  useEffect(() => {
    if (!nurses?.length) {
      setOrderedNurses([]);
      return;
    }
    const activeNurses = nurses.filter((n) => n.active === 1);
    setOrderedNurses(sortNurseListByRoleThenSequence([...activeNurses]));
  }, [nurses]);

  const { data: weeklyOffData } = useQuery({
    queryKey: [
      "weeklyOffNurses",
      currentScheduleInfo.year,
      currentScheduleInfo.month,
      isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined,
    ],
    queryFn: () =>
      weeklyOffNurses({
        year: currentScheduleInfo.year,
        month: currentScheduleInfo.month,
        groupId: isAdminHook
          ? (selectedGroupIdFromStore ?? undefined)
          : undefined,
      }),
    enabled:
      !!currentScheduleInfo.year &&
      !!currentScheduleInfo.month &&
      !!showWeeklyOff,
  });

  return (
    <CustomModal.Panel
      isLoading={isSubmissionLoading || isShiftRequestsLoading}
      loadingMsg="원티드 요청 현황을 불러오는 중입니다."
      onClose={onClose}
    >
      <div
        className="ModalSubmittedWantedList relative flex max-h-[inherit] min-h-0 flex-1 flex-col overflow-hidden bg-white"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className=" relative shrink-0 px-[60px] pt-[29px] text-center text-[20px] font-semibold font-pretendard text-[#333333]">
          <h3>
            전체 근무자 <span className="text-sig">원티드 요청 현황</span>
          </h3>
          <button
            type="button"
            aria-label="닫기"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-3 right-3"
          >
            <X size={32} strokeWidth={2} className="text-[#939399]" />
          </button>
        </div>
        <div className="modal_wantedList_body min-h-0 flex-1 overflow-y-auto scrollbar-float">
          <div className="relative">
            <div className="rosterTable">
              <div className="sticky -top-6 z-10 bg-background border-b">
                <TableHeader
                  defaultShiftCode={
                    defaultShiftCode as unknown as type.availableShifts[]
                  }
                  copyNurses={orderedNurses}
                  setCopyNurses={setOrderedNurses}
                  currentYear={currentScheduleInfo.year}
                  currentMonth={currentScheduleInfo.month}
                  holidayDates={holidayDates ?? []}
                  selectionFocusOnClick={false}
                  // 전체 현황 모달은 전월 tail 데이터가 없음
                  prevTailData={null}
                />
              </div>
              <TableBody_wanted
                nurses={orderedNurses}
                shiftInfo={shiftInfo as unknown as type.availableShifts[]}
                currentSubmittedList={currentSubmittedList}
                fullWanted={fullWanted}
                preferenceSchedule={preferenceScheduleForTable}
                assignmentsByNurse={wantedAssignmentsByNurse}
                isViewOnlyOff={isViewOnlyOff}
                currentYear={currentScheduleInfo.year}
                currentMonth={currentScheduleInfo.month}
                showWeeklyOff={showWeeklyOff}
                weeklyOffData={weeklyOffData}
              />
            </div>
          </div>
        </div>
      </div>
    </CustomModal.Panel>
  );
}

export default ModalSubmittedWantedlist;
